import React, {
  useState,
  useEffect,
  useContext,
  // useCallback,
  // memo,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Grid,
  // Select,
  // Stack,
} from "@mui/material";
import { css } from "@emotion/css";
import ClearIcon from "@mui/icons-material/Clear";
import { DateTime } from "luxon";
// import DeletePlayerConnectionIcon from "../../../../assets/images/Svg/DeletePlayerConnectionIcon";
// import { useStyles } from "./ClubsDrawer.styles";

// import { P } from "../../SuperAdminService";
import { CREATE_CLUB, EDIT_CLUB } from "../../../../graphql/clubGraphql";
// import { GET_RESOLVE_USERS } from "../../../../graphql/userGraphql";
import { DELETE_IMAGE, UPLOAD_IMAGE } from "../../../../graphql/imageGraphql";
import { GET_FIELDS } from "../../../../graphql/fieldGraphql";

import Form from "../../../../Components/Form/Form";
import LocationRenderer from "../../../../Components/Form/LocationRenderer";
import ToggleRenderer from "../../../../Components/Form/ToggleRenderer";
import MultipleFilesUpload from "../../../../Components/Form/MultipleFilesUpload";
import DrawerForm from "../../../../Components/Common/DrawerForm/DrawerForm";
import DrawerFormTitle from "../../../../Components/Common/DrawerFormTitle/DrawerFormTitle";
import {
  formValidator,
  isEmpty,
  isEmptyObject,
  isYearAfterCurrent,
} from "../../../../Components/Form/validators";
import TextFieldRenderer from "../../../../Components/Form/TextFieldRenderer";
import SelectRenderer from "../../../../Components/Form/SelectRenderer";
import DatePickerRenderer from "../../../../Components/Form/DatePickerRenderer";
import AutocompleteRenderer from "../../../../Components/Form/AutocompleteRenderer";
// import RemoveClubAdminConfirmationModal from "../Components/RemoveClubAdminConfirmationModal/RemoveClubAdminConfirmationModal";
// import LastClubAdminInformationModal from "../Components/LastClubAdminInformationModal/LastClubAdminInformationModal";

import {
  brandColorsOptions,
  countries,
  FILE_TYPES,
  imageMimeTypesDropzone,
} from "../../../../utility/Enum";
import { DATETIME_FORMAT, toTitleCase } from "../../../../utility/Utils";
import useGraphql from "../../../../Hooks/useGraphql";
import useLog from "../../../../Hooks/useLog";

// import { AuthContext } from "../../../../context/AuthContext";
import { SnackBarContext } from "../../../../context/SnackBarContext";
import { useTranslation } from "../../../../context/Internationalization";

const schema = (fieldLocation, t) => [
  {
    label: t("logo"),
    name: "clubLogo",
    renderer: MultipleFilesUpload,
    singleFileUpload: true,
    acceptedFileTypes: imageMimeTypesDropzone,
    row: 1,
  },
  {
    label: t("club_name"),
    name: "name",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: TextFieldRenderer,
    row: 2,
  },
  {
    label: t("abbreviation"),
    name: "abbreviation",
    maxLength: 3,
    renderer: TextFieldRenderer,
    row: 3,
  },
  {
    label: t("linkName"),
    name: "linkName",
    renderer: TextFieldRenderer,
    row: 4,
  },
  {
    label: t("founded"),
    name: "founded",
    views: ["year"],
    validators: [
      {
        validator: isYearAfterCurrent,
        text: (label) => `${label} ${t("cannot_be_in_future")}`,
      },
    ],
    minDate: DateTime.fromFormat("1857/01/01 00:00", DATETIME_FORMAT),
    maxDate: DateTime.now(),
    inputFormat: "yyyy",
    renderer: DatePickerRenderer,
    row: 5,
  },
  {
    label: t("country"),
    name: "country",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    options:
      countries?.map((country) => ({
        value: country.label,
        text: country.label,
      })) || [],
    renderer: SelectRenderer,
    row: 6,
  },
  {
    label: t("email"),
    name: "email",
    renderer: TextFieldRenderer,
    row: 7,
  },
  {
    label: t("website"),
    name: "website",
    renderer: TextFieldRenderer,
    row: 8,
  },
  {
    label: `${t("club_color")} 1`,
    name: "primaryColor",
    renderer: SelectRenderer,
    options: brandColorsOptions(t) || [],
    colorPicker: true,
    row: 9,
  },
  {
    label: `${t("club_color")} 2`,
    name: "secondaryColor",
    renderer: SelectRenderer,
    options: brandColorsOptions(t) || [],
    colorPicker: true,
    row: 10,
  },
  {
    label: `${t("club_color")} 3`,
    name: "tertiaryColor",
    renderer: SelectRenderer,
    options: brandColorsOptions(t) || [],
    colorPicker: true,
    row: 11,
  },
  {
    label: t("isClaimed"),
    name: "isClaimed",
    renderer: ToggleRenderer,
    row: 12,
  },
  {
    label: t("club_field"),
    name: "field",
    renderer: AutocompleteRenderer,
    query: GET_FIELDS,
    queryVariables: { skip: 0, limit: 20 },
    initialKey: "fieldIds",
    mapResults: (res) =>
      res.getFields?.map((field) => ({
        value: field._id,
        label: toTitleCase(field.name),
        text: (
          <Box display="flex" flexDirection="column">
            <strong>{toTitleCase(field.name)}</strong>
            <span style={{ fontSize: 12, color: "#888" }}>
              {field.location?.address}
            </span>
          </Box>
        ),
      })) || [],
    placeholder: t("search_by_field_name_or_address"),
    row: 13,
  },
  {
    label: t("field_location"),
    name: "fieldMap",
    renderer: LocationRenderer,
    isReadOnly: true,
    row: 14,
    hidden: !fieldLocation,
  },
  {
    label: t("club_location"),
    name: "location",
    renderer: LocationRenderer,
    row: 15,
  },
];

const initialState = {
  name: "",
  abbreviation: "",
  linkName: "",
  founded: "",
  country: "Greece",
  primaryColor: "#1B2028",
  secondaryColor: "",
  tertiaryColor: "",
  website: "",
  email: "",
  field: "",
  clubLogo: null,
  initialClubLogo: null,
  isClaimed: false,
  location: {},
  errors: {},
  // managers: [],
};

const populateData = (club) => {
  const _initialState = { ...initialState };
  if (club) {
    _initialState.name = club.name;
    _initialState.abbreviation = club.abbreviation || "";
    _initialState.linkName = club.linkName || "";
    _initialState.founded = club.founded
      ? DateTime.fromObject({ year: club.founded })
      : "";
    _initialState.country = club.country || "";
    _initialState.website = club.contactInfo?.website || "";
    _initialState.email = club.contactInfo?.email || "";
    _initialState.field =
      club.fieldInfo?._id && club.fieldInfo?.name
        ? { value: club.fieldInfo._id, label: toTitleCase(club.fieldInfo.name) }
        : "";
    _initialState.fieldMap =
      _initialState.field.length > 0
        ? {
            address: club.fieldInfo?.location?.address,
            lat: club.fieldInfo?.location?.coordinates[1],
            lng: club.fieldInfo?.location?.coordinates[0],
          }
        : null;
    _initialState.clubLogo = club.logo ? [club.logo] : null;
    _initialState.initialClubLogo = _initialState.clubLogo;
    _initialState.isClaimed = club.isClaimed;
    _initialState.location =
      club.location && Object.entries(club.location).length > 0
        ? {
            address: club.location?.address,
            lat: club.location?.coordinates[1],
            lng: club.location?.coordinates[0],
          }
        : {};
    _initialState.primaryColor = club.color?.primary || "";
    _initialState.secondaryColor = club.color?.secondary || "";
    _initialState.tertiaryColor = club.color?.tertiary || "";
    // _initialState.managers = club.managers;
  }

  return _initialState;
};

const ClubsDrawer = ({ openDrawer, onCloseDrawer, selectedClub, getClubs }) => {
  // const { authState } = useContext(AuthContext);
  const snackBar = useContext(SnackBarContext);
  // const clubId = authState.club?._id;
  const [values, setValues] = useState(populateData(selectedClub));
  const [fieldLocation, setFieldLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  // const [removeModal, setRemoveModal] = useState(false);
  // const [lastAdminModal, setLastAdminModal] = useState(false);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  // const styles = useStyles();
  const _schema = schema(fieldLocation, t);

  useEffect(() => {
    if (selectedClub) {
      setValues(populateData(selectedClub));
    } else {
      setValues(initialState);
    }
    setLoading(false);
  }, [selectedClub?._id]);

  useEffect(() => {
    if (values.field) {
      axiosGraphQL({
        query: GET_FIELDS,
        variables: {
          fieldIds: [
            typeof values.field === "object"
              ? values.field.value
              : values.field,
          ],
        },
      })
        .then((res) => {
          const field = res.data.data.getFields[0];
          if (field?.location?.coordinates?.length === 2) {
            const address = field.location.address;
            const [lng, lat] = field.location.coordinates;
            const fieldLoc = {
              address,
              lat,
              lng,
            };
            setFieldLocation(fieldLoc);
            setValues((prevState) => ({
              ...prevState,
              fieldMap: fieldLoc,
            }));
          } else {
            setFieldLocation(null);
            setValues((prevState) => ({
              ...prevState,
              fieldMap: null,
            }));
          }
        })
        .catch((err) => {
          errorLog(err, "ClubsDrawer", "useEffect", "GET_FIELDS");
          setFieldLocation(null);
          setValues((prevState) => ({
            ...prevState,
            fieldMap: null,
          }));
        });
    } else {
      setFieldLocation(null);
      setValues((prevState) => ({
        ...prevState,
        fieldMap: null,
      }));
    }
  }, [values.field]);

  const onClose = () => {
    if (loading) return;
    setValues(initialState);
    onCloseDrawer();
  };

  const handleSubmit = async () => {
    if (loading) return; // NEW
    const errors = formValidator({ schema: _schema, values });
    if (Object.keys(errors).length > 0) {
      setValues((prevState) => ({ ...prevState, errors }));
      return;
    }

    setLoading(true);
    try {
      let query = CREATE_CLUB;
      let variables = {
        club: {
          name: values.name,
          abbreviation: values.abbreviation,
          linkName: values.linkName,
          founded: values.founded ? new DateTime(values.founded).year : 0,
          country: values.country,
          color: {
            primary: values.primaryColor,
            secondary: values.secondaryColor,
            tertiary: values.tertiaryColor,
          },
          contactInfo: {
            email: values.email,
            website: values.website,
          },
          field:
            typeof values.field === "object"
              ? values.field.value
              : values.field,
          isClaimed: values.isClaimed,
          location:
            values.location &&
            Object.entries(values.location).length > 0 &&
            values.location?.address?.length > 0
              ? {
                  type: "Point",
                  address: values.location.address,
                  coordinates: [values.location.lng, values.location.lat],
                }
              : null,
        },
      };

      if (selectedClub) {
        query = EDIT_CLUB;
        variables = {
          clubId: selectedClub?._id,
          club: {
            name: values.name,
            abbreviation: values.abbreviation,
            linkName: values.linkName,
            founded: values.founded ? new DateTime(values.founded).year : 0,
            country: values.country,
            color: {
              primary: values.primaryColor,
              secondary: values.secondaryColor,
              tertiary: values.tertiaryColor,
            },
            contactInfo: {
              email: values.email,
              website: values.website,
            },
            field:
              typeof values.field === "object"
                ? values.field.value
                : values.field,
            isClaimed: values.isClaimed,
            location:
              values.location &&
              Object.entries(values.location).length > 0 &&
              values.location?.address?.length > 0
                ? {
                    type: "Point",
                    address: values.location.address,
                    coordinates: [values.location.lng, values.location.lat],
                  }
                : null,
          },
        };
      }

      const response = await axiosGraphQL({ query, variables });
      const clubId =
        query === CREATE_CLUB
          ? response.data.data.createClub._id
          : selectedClub._id;

      await uploadClubLogo(clubId); // success toast + close inside
    } catch (error) {
      errorLog(error, "ClubsDrawer", "handleSubmit", "CREATE/EDIT_CLUB");
      snackBar.setOptions({
        alertMessage: error.message,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setLoading(false); // NEW
    }
  };

  const uploadClubLogo = async (clubId) => {
    let alertMessage = t("academyCreatedSuccessfully");
    if (selectedClub?._id) alertMessage = t("academyUpdatedSuccessfully");

    // delete previous image if needed
    try {
      if (values.initialClubLogo && values.initialClubLogo.length > 0) {
        const shouldBeDeleted =
          values.clubLogo?.length === 0 ||
          (values.clubLogo?.[0]?.name !== undefined &&
            values.clubLogo?.[0]?.name !==
              values.initialClubLogo[0].split("/").pop());
        if (shouldBeDeleted) {
          const deleteVariables = {
            deleteType: FILE_TYPES.clubLogo,
            itemId: clubId,
            fileName: values.initialClubLogo[0].split("/").pop(),
          };
          await axiosGraphQL({
            query: DELETE_IMAGE,
            variables: deleteVariables,
          });
          if (values.clubLogo?.length === 0) {
            await getClubs();
          }
        }
      }
    } catch (error) {
      errorLog(error, "ClubsDrawer", "uploadClubLogo", "DELETE_IMAGE");
      snackBar.setOptions({
        alertMessage: t("error_uploading_image"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      // continue — user might still be adding a new one
    }

    // upload new image if provided
    if (
      values.clubLogo &&
      values.clubLogo.length > 0 &&
      values.clubLogo[0].name
    ) {
      try {
        const variables = {
          uploadType: FILE_TYPES.clubLogo,
          itemId: clubId,
          fileName: values.clubLogo[0].name,
        };
        const uploadRes = await axiosGraphQL({
          query: UPLOAD_IMAGE,
          variables,
        });
        const putUrl = uploadRes.data.data.uploadImage;

        // Simpler than FileReader
        const arrayBuffer = await values.clubLogo[0].arrayBuffer();
        const blob = new Blob([new Uint8Array(arrayBuffer)]);

        await fetch(putUrl, { method: "PUT", body: blob });

        snackBar.setOptions({ alertMessage, alertSeverity: "success" });
        snackBar.setSnackbarOpen(true);
        await getClubs();
        onClose();
      } catch (error) {
        errorLog(error, "ClubsDrawer", "uploadClubLogo", "UPLOAD_IMAGE");
        snackBar.setOptions({
          alertMessage: t("error_uploading_image"),
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      }
    } else {
      // no new image to upload
      snackBar.setOptions({ alertMessage, alertSeverity: "success" });
      snackBar.setSnackbarOpen(true);
      await getClubs();
      onClose();
    }
  };

  const onChange = ({ target: { name, value } }) => {
    if (loading) return; // NEW: optional guard
    setValues((prevState) => ({ ...prevState, [name]: value }));
  };

  // const handleRemoveClubAdmin = useCallback(() => {
  //   axiosGraphQL({
  //     query: REMOVE_CLUB_ADMIN_SA,
  //     variables: {
  //       clubId: selectedClub?._id,
  //       userId: removeModal?._id,
  //       deleteUser: false,
  //     },
  //   })
  //     .then(async (_res) => {
  //       snackBar.setOptions({
  //         alertMessage: t("club_admin_successfully_removed"),
  //         alertSeverity: "success",
  //       });
  //       snackBar.setSnackbarOpen(true);
  //       closeModals();
  //       await getClubs();
  //       onClose();
  //     })
  //     .catch((error) => {
  //       errorLog(
  //         error,
  //         "ClubsDrawer",
  //         "handleRemoveClubAdmin",
  //         "REMOVE_CLUB_ADMIN_SA"
  //       );
  //       snackBar.setOptions({
  //         alertMessage: error.message,
  //         alertSeverity: "error",
  //       });
  //       snackBar.setSnackbarOpen(true);
  //     });
  // }, [selectedClub?._id, removeModal?._id]);

  // const openRemoveModal = (user) => {
  //   setRemoveModal(user);
  // };

  // const closeModals = () => {
  //   setRemoveModal(false);
  //   setLastAdminModal(false);
  // };

  // const openLastAdminModal = (user) => {
  //   setLastAdminModal(user);
  // };

  return (
    <DrawerForm openDrawer={openDrawer} onCloseDrawer={onClose}>
      {/* <RemoveClubAdminConfirmationModal
        open={removeModal}
        onClose={closeModals}
        onRemove={handleRemoveClubAdmin}
      />
      <LastClubAdminInformationModal
        open={lastAdminModal}
        onClose={closeModals}
      /> */}

      <DrawerFormTitle
        actionText={selectedClub ? t("update") : t("create")}
        className="grid-container-header"
        text={t("create_club")}
      />
      <Grid item xs={12}>
        <Form
          schema={_schema}
          values={values}
          onChange={onChange}
          errors={values.errors}
        />
      </Grid>
      <Grid item xs={12} style={{ paddingTop: 24 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          fullWidth
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          {loading ? t("saving") : t("save")}
        </Button>
      </Grid>
    </DrawerForm>
  );
};

// const PhoneComponents = memo(
//   ({ userId, onRemoveClubAdmin, axiosGraphQL, errorLog, snackBar }) => {
//     const styles = useStyles();
//     const [user, setUser] = useState({
//       _id: userId,
//       firstName: "",
//       lastName: "",
//       phone: "",
//     });

//     useEffect(() => {
//       axiosGraphQL({
//         query: GET_RESOLVE_USERS,
//         variables: { ids: [userId] },
//       })
//         .then((response) => {
//           const { resolveUserIds } = response.data.data;
//           setUser(resolveUserIds[0]);
//         })
//         .catch((error) => {
//           errorLog(error, "ClubsDrawer", "useEffect", "GET_RESOLVE_USERS");
//           snackBar.setOptions({
//             alertMessage: error.message,
//             alertSeverity: "error",
//           });
//           snackBar.setSnackbarOpen(true);
//         });
//     }, []);

//     const handleRemoveClubAdmin = useCallback(() => {
//       onRemoveClubAdmin(user);
//     }, [user]);

//     if (!user) {
//       return null;
//     }

//     return (
//       <Stack
//         direction={"row"}
//         alignItems={"center"}
//         className={styles.phoneContainer}
//       >
//         <Grid container spacing={1}>
//           <Grid item className={styles.phoneComponentName}>
//             {user?.firstName} {user?.lastName}
//           </Grid>
//           <Grid item className={styles.phoneComponentNumber}>
//             {user?.mobile}
//           </Grid>
//         </Grid>
//         <IconButton color={"neutral"} onClick={handleRemoveClubAdmin}>
//           <DeletePlayerConnectionIcon />
//         </IconButton>
//       </Stack>
//     );
//   }
// );

export default ClubsDrawer;
